import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { BsChevronDown, BsChevronUp } from 'react-icons/bs'
import {
    MdAccountBalance,
    MdAutoAwesome,
    MdAutorenew,
    MdBusiness,
    MdCancel,
    MdCheckCircle,
    MdError,
    MdInfo,
    MdPause,
    MdPayment,
    MdPlayArrow,
    MdReceipt,
    MdRefresh,
    MdSchedule,
    MdShoppingCart,
    MdWarning,
} from 'react-icons/md'
import ConfirmModal from '@/components/confirm-modal'
import UsageCard from './usage-card'
import {
    getAiCreditBalance,
    hasCreditBalanceToShow,
    type AiCreditBalance,
} from '@/lib/api/core/ai-credits-api'
import { AiCreditBalanceSummary } from '@/components/ai-credits/ai-credit-balance-summary'
import { getDashboardData } from '@/lib/api/dashboard/dashboard-api'
import {
    cancelRecurringPurchase,
    listRecurringPurchases,
    pauseRecurringPurchase,
    RecurringPurchase,
    resumeRecurringPurchase,
} from '@/lib/api/payment/payment-api'
import { getPartnerPricing } from '@/lib/api/utils/settings-api'
import { CompanySegment } from '@/lib/api/common/interfaces/company.interface'
import { isIndividualCompany } from '@/lib/person-type'
import AutoPaymentCard from '@/components/auto-payment/auto-payment-card'
import {
    getSubscriptionOverview,
    paySubscription,
    SubscriptionOpenBilling,
} from '@/lib/api/subscription-billing/subscription-billing-api'
import { withCheckoutReturn } from '@/lib/billing/checkout-return-url'
import { useAuth } from '@/store/hooks'
import { formatPrice, formatDateTime, getSubscriptionStatusConfig } from '../account-utils'

/**
 * Aba financeiro: consumo do plano e compras recorrentes. A compra de pacotes
 * extras e de créditos de IA mora na Loja do Sistema (`/system-store`); os
 * botões de cada card levam para lá, já no produto.
 *
 * As estatisticas e os pacotes eram carregados no mount da tela inteira — ou
 * seja, tambem para quem so ia trocar a senha. Aqui carregam quando a aba
 * abre, que e quando o dado e olhado.
 */
export default function FinancialTab() {
    const { user, isCompany } = useAuth()
    const navigate = useNavigate()

    const isHealthSegment =
        (user?.company as any)?.segment === CompanySegment.HEALTH

    // Conta de pessoa fisica: a "empresa" E o titular, e nao ha dados fiscais.
    const isCompanyIndividual = isIndividualCompany(user?.company)

    const [loading, setLoading] = useState(false)
    const [dashboardData, setDashboardData] = useState<any>(null)
    const [creditBalance, setCreditBalance] = useState<AiCreditBalance | null>(null)
    const [platformPercentage, setPlatformPercentage] = useState<number | null>(
        null
    )

    const [showTeleconsultationLimit, setShowTeleconsultationLimit] =
        useState(false)
    const [showFiscalNotesLimit, setShowFiscalNotesLimit] = useState(false)


    const [recurringPurchases, setRecurringPurchases] = useState<
        RecurringPurchase[]
    >([])
    const [loadingRecurring, setLoadingRecurring] = useState(false)
    const [recurringLoaded, setRecurringLoaded] = useState(false)
    const [recurringConfirm, setRecurringConfirm] = useState<{
        id: number
        action: 'cancel' | 'pause'
    } | null>(null)
    const [recurringActionLoading, setRecurringActionLoading] = useState(false)

    /**
     * Compra de pacote e pagamento da mensalidade geram uma COBRANÇA e levam ao
     * checkout público dela (`/pay/:token`) — o mesmo que os clientes já usam.
     * Antes iam para `/checkout`, que criava um `Payment` fora da política de
     * taxas, da conciliação e dos relatórios. Ver docs/handoff-checkout-legado.md.
     */
    const [issuingBilling, setIssuingBilling] = useState(false)
    // Cobranças de assinatura/pacote ainda não pagas. A rotina diária emite a
    // mensalidade sozinha, então elas aparecem aqui sem ninguém ter clicado em
    // nada — e é por aqui que o assinante paga.
    const [openBillings, setOpenBillings] = useState<SubscriptionOpenBilling[]>(
        [],
    )

    const loadOpenBillings = useCallback(async () => {
        try {
            const response = await getSubscriptionOverview()
            if (response.success && response.result) {
                setOpenBillings(response.result.openBillings || [])
            }
        } catch {
            // Silencioso: é um complemento da tela. O botão de pagar continua
            // funcionando mesmo se a lista não carregar.
        }
    }, [])

    useEffect(() => {
        void loadOpenBillings()
    }, [loadOpenBillings])

    const goToBillingCheckout = (payUrl: string | null) => {
        if (!payUrl) {
            toast.error(
                'Cobrança gerada, mas sem link de pagamento. Consulte o financeiro.',
            )
            return
        }
        // `return_url` + `from=app`: o checkout roda fora do layout do sistema, e sem
        // isso a tela de "Pagamento Confirmado" terminava sem nenhuma volta.
        window.location.href = withCheckoutReturn(payUrl, 'app')
    }

    const handlePaySubscription = async () => {
        setIssuingBilling(true)
        try {
            const response = await paySubscription()
            if (response.success && response.result) {
                goToBillingCheckout(response.result.payUrl)
            } else {
                toast.error(response.error || 'Erro ao gerar a cobrança')
            }
        } catch {
            toast.error('Erro ao gerar a cobrança da assinatura')
        } finally {
            setIssuingBilling(false)
        }
    }

    const loadStats = async () => {
        if (!user?.companyId) return

        setLoading(true)
        try {
            const response = await getDashboardData()

            if (response.success) {
                setDashboardData(response.result)
            } else {
                console.error(
                    'Erro ao carregar dados do dashboard:',
                    response.error
                )
            }
        } catch (error) {
            console.error('Erro ao carregar estatísticas:', error)
        } finally {
            setLoading(false)
        }
    }

    const loadRecurringPurchases = useCallback(async () => {
        setLoadingRecurring(true)
        try {
            const response = await listRecurringPurchases()
            if (response.success && response.result) {
                setRecurringPurchases(response.result)
                setRecurringLoaded(true)
            }
        } catch (error) {
            console.error('Erro ao carregar compras recorrentes:', error)
        } finally {
            setLoadingRecurring(false)
        }
    }, [])

    const handleRecurringAction = async (
        id: number,
        action: 'cancel' | 'pause' | 'resume'
    ) => {
        setRecurringActionLoading(true)
        try {
            const actionFn =
                action === 'cancel'
                    ? cancelRecurringPurchase
                    : action === 'pause'
                      ? pauseRecurringPurchase
                      : resumeRecurringPurchase
            const response = await actionFn(id)
            if (response.success) {
                const labels = {
                    cancel: 'cancelada',
                    pause: 'pausada',
                    resume: 'retomada',
                }
                toast.success(`Recorrência ${labels[action]} com sucesso`)
                loadRecurringPurchases()
            } else {
                toast.error(
                    (response as any).error || 'Erro ao atualizar recorrência'
                )
            }
        } catch (error) {
            toast.error('Erro ao atualizar recorrência')
        } finally {
            setRecurringActionLoading(false)
            setRecurringConfirm(null)
        }
    }

    useEffect(() => {
        if (!user?.companyId) return
        void getAiCreditBalance().then(response => {
            if (response.success) setCreditBalance(response.result)
        })
        void loadStats()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.companyId])

    useEffect(() => {
        void loadRecurringPurchases()
    }, [loadRecurringPurchases])

    // Split da plataforma: so faz sentido para conta de empresa.
    useEffect(() => {
        if (!isCompany || platformPercentage !== null) return
        getPartnerPricing()
            .then(res => {
                if (res.success) setPlatformPercentage(res.result.percentage)
            })
            .catch(() => {})
    }, [isCompany, platformPercentage])

    return (
        <>
            <div className="flex flex-col gap-6">
                {/*
                    Consumo do plano e compra de pacotes extras.
                    Ficava ACIMA das abas, então aparecia em TODAS
                    elas — quem ia trocar a senha ou ver notificações
                    rolava por esses cards antes de chegar ao que
                    procurava. Mora aqui porque é informação da
                    assinatura: o que o plano dá e o quanto já foi
                    consumido.
                */}
                {/* Os cards se dividem pelo espaco disponivel em vez de assumir
                    quatro colunas fixas: teleconsulta esta desativada e outros
                    somem por tipo de conta, entao um grid de 4 deixava os
                    restantes espremidos com espaco sobrando ao lado. */}
                {/* Créditos de IA: saldo, de onde vem e o gasto do mês. */}
                {hasCreditBalanceToShow(creditBalance) && (
                    <div className="bg-base-100 rounded-xl border border-base-300/50 shadow p-5 sm:p-6">
                        <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
                            <div className="flex items-center gap-3">
                                <div className="w-9 h-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                                    <MdAutoAwesome className="text-lg" />
                                </div>
                                <h3 className="text-lg font-semibold">Créditos de IA</h3>
                            </div>
                            <button
                                type="button"
                                className="btn btn-sm btn-primary"
                                onClick={() => navigate('/system-store?product=ai-credits')}
                            >
                                <MdShoppingCart className="text-lg" />
                                Comprar créditos
                            </button>
                        </div>
                        <AiCreditBalanceSummary balance={creditBalance} showUsage />
                    </div>
                )}

                <div className="flex flex-wrap gap-4 [&>*]:min-w-[13rem] [&>*]:flex-1">

                    {/*
                        Card de teleconsulta desativado para TODOS os tipos de conta.
                        O painel expandido e o modal de pacotes continuam no arquivo,
                        logo abaixo — sem este card, `showTeleconsultationLimit` nunca
                        vira true e eles ficam inalcançáveis. Para reativar, basta
                        descomentar este bloco; nada mais precisa voltar.

                    {isHealthSegment && dashboardData?.teleconsultation && (
                        <div
                            className={`bg-gradient-to-r from-secondary/10 to-secondary/10 px-6 py-4 rounded-xl border border-base-300/50 shadow cursor-pointer   hover:from-secondary/15 hover:to-secondary/15 ${showTeleconsultationLimit ? 'ring-2 ring-secondary/30' : ''}`}
                            onClick={() =>
                                setShowTeleconsultationLimit(
                                    !showTeleconsultationLimit
                                )
                            }
                        >
                            <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-lg bg-secondary/20 flex items-center justify-center">
                                    <MdSchedule className="text-secondary text-lg" />
                                </div>
                                <h2 className="text-base font-bold text-base-content">
                                    Teleconsultas do Mês
                                </h2>
                                <div className="ml-auto">
                                    {showTeleconsultationLimit ? (
                                        <BsChevronUp />
                                    ) : (
                                        <BsChevronDown />
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                    */}

                    {/*
                        Conta de pessoa física não emite nota fiscal, então nem o
                        consumo nem a compra de notas extras fazem sentido — vender
                        pacote de algo que a conta não pode usar seria cobrar por
                        nada. Ver docs/person-type-pf-pj.md §5.
                    */}
                    {!isCompanyIndividual && dashboardData?.fiscalNotes && (
                        <UsageCard
                            icon={<MdReceipt className="text-lg" />}
                            title="Notas fiscais"
                            current={dashboardData.fiscalNotes.currentCount}
                            max={dashboardData.fiscalNotes.maxNotes}
                            isNearLimit={dashboardData.fiscalNotes.isNearLimit}
                            isAtLimit={dashboardData.fiscalNotes.isAtLimit}
                            expanded={showFiscalNotesLimit}
                            onToggle={() =>
                                setShowFiscalNotesLimit(!showFiscalNotesLimit)
                            }
                        />
                    )}
                </div>

                {/* Painel expandido - Teleconsulta */}
                {isHealthSegment &&
                    showTeleconsultationLimit &&
                    dashboardData?.teleconsultation && (
                        <div className="bg-gradient-to-br from-base-100 to-base-200/50 rounded-xl border border-base-300/50 shadow p-6">
                            <div className="flex flex-col gap-4">
                                <div className="flex items-center justify-between">
                                    <div className="flex flex-col gap-1">
                                        <span className="text-sm text-base-content/60">
                                            Utilizados este mês
                                        </span>
                                        <div className="flex items-baseline gap-2">
                                            <span className="text-3xl font-bold text-base-content">
                                                {
                                                    dashboardData
                                                        .teleconsultation
                                                        .currentMinutes
                                                }
                                            </span>
                                            <span className="text-lg text-base-content/60">
                                                /
                                                {dashboardData.teleconsultation
                                                    .maxMinutes === 'unlimited'
                                                    ? '∞'
                                                    : dashboardData
                                                          .teleconsultation
                                                          .maxMinutes}{' '}
                                                min
                                            </span>
                                        </div>
                                    </div>
                                    <div className="flex flex-col items-end gap-1">
                                        <span className="text-sm text-base-content/60">
                                            Disponíveis
                                        </span>
                                        <span className="text-2xl font-bold text-secondary">
                                            {dashboardData.teleconsultation
                                                .maxMinutes === 'unlimited'
                                                ? 'Ilimitado'
                                                : Math.max(
                                                      0,
                                                      dashboardData
                                                          .teleconsultation
                                                          .maxMinutes -
                                                          dashboardData
                                                              .teleconsultation
                                                              .currentMinutes
                                                  )}{' '}
                                            {dashboardData.teleconsultation
                                                .maxMinutes !== 'unlimited' &&
                                                'min'}
                                        </span>
                                    </div>
                                </div>

                                <div className="flex flex-col gap-2">
                                    <div className="w-full bg-base-300/30 rounded-full h-6 overflow-hidden relative">
                                        <div
                                            className={`h-full rounded-full transition-all duration-500 ${
                                                dashboardData.teleconsultation
                                                    .isAtLimit
                                                    ? 'bg-error'
                                                    : dashboardData
                                                            .teleconsultation
                                                            .isNearLimit
                                                      ? 'bg-warning'
                                                      : 'bg-secondary'
                                            }`}
                                            style={{
                                                width: `${
                                                    dashboardData
                                                        .teleconsultation
                                                        .maxMinutes ===
                                                    'unlimited'
                                                        ? 0
                                                        : Math.min(
                                                              dashboardData
                                                                  .teleconsultation
                                                                  .percentage,
                                                              100
                                                          )
                                                }%`,
                                            }}
                                        />
                                        <div className="absolute inset-0 flex items-center justify-center">
                                            <span className="text-xs font-semibold text-base-content">
                                                {dashboardData.teleconsultation.percentage.toFixed(
                                                    1
                                                )}
                                                %
                                            </span>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2 text-xs">
                                        {dashboardData.teleconsultation
                                            .isAtLimit ? (
                                            <>
                                                <MdError className="text-error text-base" />
                                                <span className="text-error font-medium">
                                                    Limite atingido
                                                </span>
                                            </>
                                        ) : dashboardData.teleconsultation
                                              .isNearLimit ? (
                                            <>
                                                <MdWarning className="text-warning text-base" />
                                                <span className="text-warning font-medium">
                                                    Próximo do limite (
                                                    {dashboardData.teleconsultation.percentage.toFixed(
                                                        1
                                                    )}
                                                    %)
                                                </span>
                                            </>
                                        ) : (
                                            <>
                                                <MdCheckCircle className="text-success text-base" />
                                                <span className="text-success font-medium">
                                                    Dentro do limite
                                                </span>
                                            </>
                                        )}
                                    </div>
                                </div>

                                <div className="mt-4 pt-4 border-t border-base-300/50">
                                    <button
                                        onClick={() => navigate('/system-store')}
                                        className="btn btn-secondary w-full"
                                    >
                                        <MdShoppingCart className="text-lg" />
                                        Comprar Minutos Extras
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                {/* Painel expandido - Notas Fiscais */}
                {!isCompanyIndividual &&
                    showFiscalNotesLimit &&
                    dashboardData?.fiscalNotes && (
                    <div className="bg-gradient-to-br from-base-100 to-base-200/50 rounded-xl border border-base-300/50 shadow p-6">
                        <div className="flex flex-col gap-4">
                            <div className="flex items-center justify-between">
                                <div className="flex flex-col gap-1">
                                    <span className="text-sm text-base-content/60">
                                        Utilizadas este mês
                                    </span>
                                    <div className="flex items-baseline gap-2">
                                        <span className="text-3xl font-bold text-base-content">
                                            {
                                                dashboardData.fiscalNotes
                                                    .currentCount
                                            }
                                        </span>
                                        <span className="text-lg text-base-content/60">
                                            /
                                            {dashboardData.fiscalNotes
                                                .maxNotes === 'unlimited'
                                                ? '∞'
                                                : dashboardData.fiscalNotes
                                                      .maxNotes}
                                        </span>
                                    </div>
                                </div>
                                <div className="flex flex-col items-end gap-1">
                                    <span className="text-sm text-base-content/60">
                                        Disponíveis
                                    </span>
                                    <span className="text-2xl font-bold text-success">
                                        {dashboardData.fiscalNotes.maxNotes ===
                                        'unlimited'
                                            ? 'Ilimitado'
                                            : Math.max(
                                                  0,
                                                  dashboardData.fiscalNotes
                                                      .maxNotes -
                                                      dashboardData.fiscalNotes
                                                          .currentCount
                                              )}
                                    </span>
                                </div>
                            </div>

                            <div className="flex flex-col gap-2">
                                <div className="w-full bg-base-300/30 rounded-full h-6 overflow-hidden relative">
                                    <div
                                        className={`h-full rounded-full transition-all duration-500 ${
                                            dashboardData.fiscalNotes.isAtLimit
                                                ? 'bg-error'
                                                : dashboardData.fiscalNotes
                                                        .isNearLimit
                                                  ? 'bg-warning'
                                                  : 'bg-success'
                                        }`}
                                        style={{
                                            width: `${
                                                dashboardData.fiscalNotes
                                                    .maxNotes === 'unlimited'
                                                    ? 0
                                                    : Math.min(
                                                          dashboardData
                                                              .fiscalNotes
                                                              .percentage,
                                                          100
                                                      )
                                            }%`,
                                        }}
                                    />
                                    <div className="absolute inset-0 flex items-center justify-center">
                                        <span className="text-xs font-semibold text-base-content">
                                            {dashboardData.fiscalNotes.percentage.toFixed(
                                                1
                                            )}
                                            %
                                        </span>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2 text-xs">
                                    {dashboardData.fiscalNotes.isAtLimit ? (
                                        <>
                                            <MdError className="text-error text-base" />
                                            <span className="text-error font-medium">
                                                Limite atingido
                                            </span>
                                        </>
                                    ) : dashboardData.fiscalNotes
                                          .isNearLimit ? (
                                        <>
                                            <MdWarning className="text-warning text-base" />
                                            <span className="text-warning font-medium">
                                                Próximo do limite (
                                                {dashboardData.fiscalNotes.percentage.toFixed(
                                                    1
                                                )}
                                                %)
                                            </span>
                                        </>
                                    ) : (
                                        <>
                                            <MdCheckCircle className="text-success text-base" />
                                            <span className="text-success font-medium">
                                                Dentro do limite
                                            </span>
                                        </>
                                    )}
                                </div>
                            </div>

                            <div className="mt-4 pt-4 border-t border-base-300/50">
                                <button
                                    onClick={() =>
                                        navigate('/system-store?product=fiscal-notes')
                                    }
                                    className="btn btn-success w-full"
                                >
                                    <MdShoppingCart className="text-lg" />
                                    Comprar Notas Fiscais Extras
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                <AutoPaymentCard />
                {(() => {
                    const subscription =
                        user?.company?.organization
                            ?.subscription

                    const plan = subscription?.plan

                    if (!subscription || !plan) {
                        return (
                            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4 text-center p-8">
                                <div className="w-20 h-20 rounded-full bg-base-200 flex items-center justify-center">
                                    <MdInfo className="text-4xl text-base-content/30" />
                                </div>
                                <h3 className="text-xl font-semibold text-base-content">
                                    Nenhuma Assinatura
                                </h3>
                                <p className="text-sm text-base-content/60 max-w-md">
                                    Você ainda não possui uma
                                    assinatura ativa.
                                </p>
                            </div>
                        )
                    }

                    const statusConfig = getSubscriptionStatusConfig(
                        subscription.status
                    )
                    const StatusIcon = statusConfig.icon

                    const recurrenceLabels: Record<
                        string,
                        string
                    > = {
                        MONTHLY: 'Mensal',
                        YEARLY: 'Anual',
                        WEEKLY: 'Semanal',
                        DAILY: 'Diário',
                    }

                    const subscriptionPrice =
                        (subscription as any).customPrice ??
                        subscription.plan?.customPrice ??
                        subscription.plan?.price ??
                        0
                    const isFreeSubscription =
                        subscriptionPrice === 0

                    const now = new Date()
                    const currentDay = now.getDate()
                    const currentMonth = now.getMonth()
                    const currentYear = now.getFullYear()
                    const dueDay = subscription.dueDay
                    const isTodayDueDay = currentDay === dueDay
                    const isDueDatePassed = currentDay > dueDay

                    const lastPaymentDate =
                        subscription.lastPaymentDate
                            ? new Date(
                                  subscription.lastPaymentDate
                              )
                            : null

                    const hasPaymentForCurrentPeriod =
                        lastPaymentDate &&
                        lastPaymentDate.getMonth() ===
                            currentMonth &&
                        lastPaymentDate.getFullYear() ===
                            currentYear

                    const showInfoAlert =
                        !isFreeSubscription &&
                        isTodayDueDay &&
                        !hasPaymentForCurrentPeriod
                    const showWarningAlert =
                        !isFreeSubscription &&
                        isDueDatePassed &&
                        !hasPaymentForCurrentPeriod

                    return (
                        <div className="flex flex-col gap-6">
                            {showInfoAlert && (
                                <div className="alert alert-info">
                                    <MdInfo className="text-xl" />
                                    <div className="flex-1">
                                        <p className="font-medium">
                                            Hoje é o dia do
                                            vencimento da sua
                                            assinatura (dia{' '}
                                            {dueDay})
                                        </p>
                                        <p className="text-sm">
                                            Você já pode
                                            realizar o
                                            pagamento.
                                        </p>
                                    </div>
                                    <button
                                        className="btn btn-sm btn-info text-white"
                                        onClick={handlePaySubscription}
                                        disabled={issuingBilling}
                                    >
                                        <MdPayment className="text-lg" />
                                        Realizar Pagamento
                                    </button>
                                </div>
                            )}

                            {showWarningAlert && (
                                <div className="alert alert-warning">
                                    <MdWarning className="text-xl" />
                                    <div className="flex-1">
                                        <p className="font-medium">
                                            Pagamento pendente!
                                            O vencimento da sua
                                            assinatura é todo
                                            dia {dueDay}
                                        </p>
                                        <p className="text-sm">
                                            Realize o pagamento
                                            para evitar
                                            interrupções.
                                        </p>
                                    </div>
                                    <button
                                        className="btn btn-sm btn-warning text-white"
                                        onClick={handlePaySubscription}
                                        disabled={issuingBilling}
                                    >
                                        <MdPayment className="text-lg" />
                                        Regularizar Pagamento
                                    </button>
                                </div>
                            )}

                            {openBillings.length > 0 && (
                                <div className="rounded-xl border border-base-300 bg-base-100 p-4">
                                    <h3 className="mb-3 text-sm font-bold uppercase tracking-wide text-base-content/60">
                                        Cobranças em aberto
                                    </h3>
                                    <div className="flex flex-col gap-2">
                                        {openBillings.map(billing => (
                                            <div
                                                key={billing.id}
                                                className="flex flex-wrap items-center justify-between gap-3 rounded-lg bg-base-200/40 px-3 py-2"
                                            >
                                                <div className="min-w-0">
                                                    <p className="truncate text-sm font-medium text-base-content">
                                                        {billing.description ||
                                                            'Cobrança'}
                                                    </p>
                                                    <p className="text-xs text-base-content/60">
                                                        Vence em{' '}
                                                        {new Date(
                                                            billing.dueDate,
                                                        ).toLocaleDateString(
                                                            'pt-BR',
                                                        )}
                                                        {billing.status ===
                                                            'OVERDUE' &&
                                                            ' · vencida'}
                                                    </p>
                                                </div>
                                                <div className="flex items-center gap-3">
                                                    <span className="font-semibold text-secondary">
                                                        {formatPrice(
                                                            billing.amount,
                                                        )}
                                                    </span>
                                                    <button
                                                        className="btn btn-sm btn-secondary"
                                                        onClick={() =>
                                                            goToBillingCheckout(
                                                                billing.payUrl,
                                                            )
                                                        }
                                                    >
                                                        <MdPayment className="text-lg" />
                                                        Pagar
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div className="flex items-center justify-between sm:flex-row flex-col gap-2">
                                <h2 className="text-xl font-bold text-base-content">
                                    Informações da Assinatura
                                </h2>
                                <div className="flex gap-2 items-center">
                                    <div
                                        className={`badge badge-lg gap-2 ${statusConfig.className}`}
                                    >
                                        <StatusIcon className="text-base" />
                                        {statusConfig.label}
                                    </div>
                                    {!isFreeSubscription &&
                                        !hasPaymentForCurrentPeriod && (
                                            <button
                                                className="btn btn-sm btn-secondary"
                                                onClick={handlePaySubscription}
                                                disabled={issuingBilling}
                                            >
                                                <MdPayment className="text-lg" />
                                                Pagamento
                                            </button>
                                        )}
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="card bg-base-200/50 border border-base-300">
                                    <div className="card-body p-4">
                                        <div className="flex items-center gap-3 mb-2">
                                            <div className="w-10 h-10 rounded-lg bg-secondary/10 flex items-center justify-center">
                                                <MdAccountBalance className="text-secondary text-xl" />
                                            </div>
                                            <div>
                                                <p className="text-xs text-base-content/60">
                                                    Plano
                                                </p>
                                                <p className="font-semibold text-base-content">
                                                    {plan.name}
                                                </p>
                                            </div>
                                        </div>
                                        {plan.description && (
                                            <p className="text-sm text-base-content/70 mt-2">
                                                {
                                                    plan.description
                                                }
                                            </p>
                                        )}
                                    </div>
                                </div>

                                {isFreeSubscription ? (
                                    <div className="card bg-base-200/50 border border-base-300">
                                        <div className="card-body p-4">
                                            <div className="flex items-center gap-3 mb-2">
                                                <div className="w-10 h-10 rounded-lg bg-success/10 flex items-center justify-center">
                                                    <MdCheckCircle className="text-success text-xl" />
                                                </div>
                                                <div>
                                                    <p className="text-xs text-base-content/60">
                                                        Valor
                                                    </p>
                                                    <p className="font-semibold text-success">
                                                        Plano
                                                        Gratuito
                                                    </p>
                                                </div>
                                            </div>
                                            <p className="text-xs text-base-content/60 mt-2">
                                                Sem cobrança
                                            </p>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="card bg-base-200/50 border border-base-300">
                                        <div className="card-body p-4">
                                            <div className="flex items-center gap-3 mb-2">
                                                <div className="w-10 h-10 rounded-lg bg-secondary/10 flex items-center justify-center">
                                                    <MdSchedule className="text-secondary text-xl" />
                                                </div>
                                                <div>
                                                    <p className="text-xs text-base-content/60">
                                                        Vencimento
                                                    </p>
                                                    <p className="font-semibold text-base-content">
                                                        Todo dia{' '}
                                                        {
                                                            subscription.dueDay
                                                        }
                                                    </p>
                                                </div>
                                            </div>
                                            <p className="text-xs text-base-content/60 mt-2">
                                                Cobrança mensal
                                            </p>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="card bg-gradient-to-br from-secondary/5 to-secondary/5 border border-secondary/20">
                                <div className="card-body p-6">
                                    <h3 className="text-lg font-bold text-base-content mb-4">
                                        Detalhes do Plano
                                    </h3>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <p className="text-xs text-base-content/60 mb-1">
                                                Preço
                                            </p>
                                            <p className="text-2xl font-bold text-secondary">
                                                {plan.isCustom &&
                                                plan.customPrice
                                                    ? formatPrice(
                                                          plan.customPrice
                                                      )
                                                    : formatPrice(
                                                          plan.price
                                                      )}
                                            </p>
                                            <p className="text-xs text-base-content/60 mt-1">
                                                {recurrenceLabels[
                                                    plan
                                                        .recurrence
                                                ] ||
                                                    plan.recurrence}
                                            </p>
                                        </div>

                                        <div>
                                            <p className="text-xs text-base-content/60 mb-1">
                                                Tipo
                                            </p>
                                            <p className="font-semibold text-base-content">
                                                {plan.isCustom
                                                    ? 'Plano Personalizado'
                                                    : 'Plano Padrão'}
                                            </p>
                                            {plan.private && (
                                                <span className="badge badge-sm badge-warning mt-1">
                                                    Privado
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    {plan.features &&
                                        Object.keys(
                                            plan.features
                                        ).length > 0 &&
                                        (() => {
                                            const healthOnlyFeatures =
                                                [
                                                    'teleconsultationMinutes',
                                                ]
                                            const featureLabels: Record<
                                                string,
                                                string
                                            > = {
                                                support:
                                                    'Suporte',
                                                departments:
                                                    'Departamentos',
                                                users: 'Usuários',
                                                aiUsers:
                                                    'Usuários de IA',
                                                teleconsultationMinutes:
                                                    'Minutos de Teleconsulta',
                                                monthlySignDocuments:
                                                    'Documentos para Assinatura/Mês',
                                                resale: 'Revenda',
                                                customizations:
                                                    'Customizações',
                                                monthlyFiscalNotes:
                                                    'Notas Fiscais/Mês',
                                                aiCredits:
                                                    'Créditos de IA/Mês',
                                            }
                                            const filteredFeatures =
                                                Object.entries(
                                                    plan.features
                                                ).filter(
                                                    ([key]) =>
                                                        isHealthSegment ||
                                                        !healthOnlyFeatures.includes(
                                                            key
                                                        )
                                                )
                                            if (
                                                filteredFeatures.length ===
                                                0
                                            )
                                                return null
                                            return (
                                                <div className="mt-6 pt-6 border-t border-base-300">
                                                    <p className="text-sm font-semibold text-base-content mb-3">
                                                        Recursos
                                                        Incluídos
                                                    </p>
                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                                        {filteredFeatures.map(
                                                            ([
                                                                key,
                                                                value,
                                                            ]) => {
                                                                const displayValue =
                                                                    typeof value ===
                                                                    'boolean'
                                                                        ? value
                                                                            ? 'Sim'
                                                                            : 'Não'
                                                                        : value ===
                                                                            'unlimited'
                                                                          ? 'Ilimitado'
                                                                          : String(
                                                                                value
                                                                            )
                                                                const label =
                                                                    featureLabels[
                                                                        key
                                                                    ] ||
                                                                    key

                                                                return (
                                                                    <div
                                                                        key={
                                                                            key
                                                                        }
                                                                        className="flex items-center gap-2 text-sm"
                                                                    >
                                                                        <MdCheckCircle className="text-success text-base flex-shrink-0" />
                                                                        <span className="text-base-content/70">
                                                                            <span className="font-medium">
                                                                                {
                                                                                    label
                                                                                }

                                                                                :
                                                                            </span>{' '}
                                                                            {
                                                                                displayValue
                                                                            }
                                                                        </span>
                                                                    </div>
                                                                )
                                                            }
                                                        )}
                                                    </div>
                                                </div>
                                            )
                                        })()}
                                </div>
                            </div>

                            {isCompany &&
                                plan.features?.resale ===
                                    true && (
                                    <div className="card bg-gradient-to-br from-success/5 to-success/10 border border-success/20">
                                        <div className="card-body p-6">
                                            <div className="flex items-center gap-3 mb-4">
                                                <div className="w-10 h-10 rounded-lg bg-success/10 flex items-center justify-center">
                                                    <MdBusiness className="text-success text-xl" />
                                                </div>
                                                <div>
                                                    <h3 className="text-lg font-bold text-base-content">
                                                        Revenda
                                                    </h3>
                                                    <p className="text-xs text-base-content/60">
                                                        Split de
                                                        receita
                                                        nas
                                                        assinaturas
                                                        de seus
                                                        clientes
                                                    </p>
                                                </div>
                                            </div>

                                            {platformPercentage !==
                                            null ? (
                                                <>
                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                        <div className="p-4 rounded-xl bg-base-100 border border-base-300">
                                                            <p className="text-xs text-base-content/60 mb-1">
                                                                Sua
                                                                receita
                                                                por
                                                                cliente
                                                            </p>
                                                            <p className="text-2xl font-bold text-success">
                                                                {100 -
                                                                    platformPercentage}

                                                                %
                                                            </p>
                                                            <p className="text-xs text-base-content/50 mt-1">
                                                                de
                                                                cada
                                                                assinatura
                                                                paga
                                                                pelo
                                                                clientes
                                                            </p>
                                                        </div>

                                                        <div className="p-4 rounded-xl bg-base-100 border border-base-300">
                                                            <p className="text-xs text-base-content/60 mb-1">
                                                                Taxa
                                                                da
                                                                plataforma
                                                            </p>
                                                            <p className="text-2xl font-bold text-base-content/70">
                                                                {
                                                                    platformPercentage
                                                                }

                                                                %
                                                            </p>
                                                            <p className="text-xs text-base-content/50 mt-1">
                                                                retido
                                                                pela
                                                                plataforma
                                                            </p>
                                                        </div>
                                                    </div>

                                                    {platformPercentage <
                                                        100 && (
                                                        <div className="mt-4 p-4 bg-base-100 rounded-xl border border-base-300">
                                                            <p className="text-xs font-medium text-base-content/60 mb-2">
                                                                Exemplo
                                                                de
                                                                cálculo
                                                            </p>
                                                            <p className="text-sm text-base-content/70">
                                                                Se
                                                                um
                                                                clientes
                                                                paga
                                                                uma
                                                                assinatura
                                                                de{' '}
                                                                <strong>
                                                                    R$
                                                                    1500,00
                                                                </strong>

                                                                ,
                                                                você
                                                                recebe{' '}
                                                                <strong className="text-success">
                                                                    R${' '}
                                                                    {(
                                                                        (1500 *
                                                                            (100 -
                                                                                platformPercentage)) /
                                                                        100
                                                                    )
                                                                        .toFixed(
                                                                            2
                                                                        )
                                                                        .replace(
                                                                            '.',
                                                                            ','
                                                                        )}
                                                                </strong>{' '}
                                                                e
                                                                a
                                                                plataforma
                                                                retém{' '}
                                                                <strong>
                                                                    R${' '}
                                                                    {(
                                                                        (1500 *
                                                                            platformPercentage) /
                                                                        100
                                                                    )
                                                                        .toFixed(
                                                                            2
                                                                        )
                                                                        .replace(
                                                                            '.',
                                                                            ','
                                                                        )}
                                                                </strong>

                                                                .
                                                                Não
                                                                estamos
                                                                incluindo
                                                                taxas
                                                                nesse
                                                                cálculo.
                                                            </p>
                                                        </div>
                                                    )}
                                                </>
                                            ) : (
                                                <div className="flex justify-center py-4">
                                                    <span className="loading loading-spinner loading-sm text-success" />
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}

                            <div className="card bg-base-200/50 border border-base-300">
                                <div className="card-body p-4">
                                    <div className="flex items-start gap-3">
                                        <div className="w-10 h-10 rounded-lg bg-info/10 flex items-center justify-center flex-shrink-0">
                                            <MdInfo className="text-info text-xl" />
                                        </div>
                                        <div className="flex-1">
                                            <p className="text-sm font-semibold text-base-content mb-1">
                                                Informações da
                                                Assinatura
                                            </p>
                                            <div className="space-y-1 text-xs text-base-content/70">
                                                <p>
                                                    <span className="font-medium">
                                                        ID da
                                                        Assinatura:
                                                    </span>{' '}
                                                    {
                                                        subscription.id
                                                    }
                                                </p>
                                                <p>
                                                    <span className="font-medium">
                                                        Criada
                                                        em:
                                                    </span>{' '}
                                                    {formatDateTime(
                                                        subscription.createdAt
                                                    )}
                                                </p>
                                                <p>
                                                    <span className="font-medium">
                                                        Última
                                                        atualização:
                                                    </span>{' '}
                                                    {formatDateTime(
                                                        subscription.updatedAt
                                                    )}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )
                })()}

                {/* Compras Recorrentes */}
                <div className="card bg-base-200/50 border border-base-300 mt-6">
                    <div className="card-body p-6">
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center">
                                    <MdAutorenew className="text-accent text-xl" />
                                </div>
                                <div>
                                    <h3 className="text-lg font-bold text-base-content">
                                        Compras Recorrentes
                                    </h3>
                                    <p className="text-xs text-base-content/60">
                                        Pacotes extras e
                                        assinaturas
                                    </p>
                                </div>
                            </div>
                            <button
                                className="btn btn-sm btn-outline btn-accent"
                                onClick={() => {
                                    setRecurringLoaded(false)
                                    loadRecurringPurchases()
                                }}
                                disabled={loadingRecurring}
                            >
                                {loadingRecurring ? (
                                    <span className="loading loading-spinner loading-sm" />
                                ) : (
                                    <>
                                        <MdRefresh className="text-lg" />
                                        Atualizar
                                    </>
                                )}
                            </button>
                        </div>

                        {loadingRecurring &&
                        !recurringLoaded ? (
                            <div className="flex items-center justify-center py-8">
                                <span className="loading loading-spinner loading-lg" />
                            </div>
                        ) : recurringPurchases.length === 0 ? (
                            <div className="text-center py-8 text-base-content/50">
                                <MdAutorenew className="text-4xl mx-auto mb-2 opacity-30" />
                                <p className="text-sm">
                                    Nenhuma compra recorrente
                                    ativa.
                                </p>
                                <p className="text-xs mt-1">
                                    Ao comprar um pacote extra,
                                    marque a opção de
                                    recorrência mensal.
                                </p>
                            </div>
                        ) : (
                            <div className="flex flex-col gap-3">
                                {recurringPurchases.map(rp => {
                                    const packLabels: Record<
                                        string,
                                        string
                                    > = {
                                        attendances:
                                            'Atendimentos',
                                        'teleconsultation-minutes':
                                            'Minutos Teleconsulta',
                                    }
                                    const isSubscription =
                                        rp.purchaseType ===
                                        'SUBSCRIPTION'
                                    const isBilling =
                                        rp.purchaseType ===
                                        'BILLING'
                                    const packLabel = isBilling
                                        ? 'Cobrança Recorrente'
                                        : isSubscription
                                          ? 'Assinatura'
                                          : rp.packType
                                            ? packLabels[
                                                  rp.packType
                                              ] || rp.packType
                                            : 'Pacote'
                                    const price = (
                                        rp.amountCents / 100
                                    ).toLocaleString('pt-BR', {
                                        style: 'currency',
                                        currency: 'BRL',
                                    })
                                    const statusMap: Record<
                                        string,
                                        {
                                            label: string
                                            className: string
                                        }
                                    > = {
                                        ACTIVE: {
                                            label: 'Ativa',
                                            className:
                                                'badge-success',
                                        },
                                        PAUSED: {
                                            label: 'Pausada',
                                            className:
                                                'badge-warning',
                                        },
                                        CANCELLED: {
                                            label: 'Cancelada',
                                            className:
                                                'badge-error',
                                        },
                                    }
                                    const status = statusMap[
                                        rp.status
                                    ] || {
                                        label: rp.status,
                                        className:
                                            'badge-ghost',
                                    }

                                    return (
                                        <div
                                            key={rp.id}
                                            className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 rounded-xl bg-base-100 border border-base-300"
                                        >
                                            <div className="flex-1">
                                                <div className="flex items-center gap-2 mb-1">
                                                    <span className="font-semibold text-base-content">
                                                        {isSubscription ||
                                                        isBilling
                                                            ? packLabel
                                                            : `${rp.quantity || 0} ${packLabel}`}
                                                    </span>
                                                    <span
                                                        className={`badge badge-sm ${status.className}`}
                                                    >
                                                        {
                                                            status.label
                                                        }
                                                    </span>
                                                </div>
                                                <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-base-content/60">
                                                    <span>
                                                        <strong>
                                                            Valor:
                                                        </strong>{' '}
                                                        {price}
                                                    </span>
                                                    <span>
                                                        <strong>
                                                            Dia
                                                            da
                                                            cobrança:
                                                        </strong>{' '}
                                                        {
                                                            rp.dueDay
                                                        }
                                                    </span>
                                                    {isBilling &&
                                                        rp
                                                            .billingData
                                                            ?.description && (
                                                            <span>
                                                                <strong>
                                                                    Descrição:
                                                                </strong>{' '}
                                                                {
                                                                    rp
                                                                        .billingData
                                                                        .description
                                                                }
                                                            </span>
                                                        )}
                                                    {rp.creditCardNumber && (
                                                        <span>
                                                            <strong>
                                                                Cartão:
                                                            </strong>{' '}
                                                            ****{' '}
                                                            {
                                                                rp.creditCardNumber
                                                            }
                                                            {rp.creditCardBrand &&
                                                                rp.creditCardBrand !==
                                                                    'UNKNOWN' &&
                                                                ` (${rp.creditCardBrand})`}
                                                        </span>
                                                    )}
                                                    {rp.nextChargeDate &&
                                                        rp.status ===
                                                            'ACTIVE' && (
                                                            <span>
                                                                <strong>
                                                                    Próxima
                                                                    cobrança:
                                                                </strong>{' '}
                                                                {new Date(
                                                                    rp.nextChargeDate
                                                                ).toLocaleDateString(
                                                                    'pt-BR'
                                                                )}
                                                            </span>
                                                        )}
                                                    {rp.failedAttempts >
                                                        0 && (
                                                        <span className="text-error">
                                                            <strong>
                                                                Falhas:
                                                            </strong>{' '}
                                                            {
                                                                rp.failedAttempts
                                                            }
                                                            /3
                                                        </span>
                                                    )}
                                                </div>
                                            </div>

                                            <div className="flex gap-2 flex-shrink-0">
                                                {rp.status ===
                                                    'ACTIVE' && (
                                                    <button
                                                        className="btn btn-xs btn-outline btn-warning"
                                                        onClick={() =>
                                                            setRecurringConfirm(
                                                                {
                                                                    id: rp.id,
                                                                    action: 'pause',
                                                                }
                                                            )
                                                        }
                                                        title="Pausar recorrência"
                                                    >
                                                        <MdPause className="text-sm" />
                                                        Pausar
                                                    </button>
                                                )}
                                                {rp.status ===
                                                    'PAUSED' && (
                                                    <button
                                                        className="btn btn-xs btn-outline btn-success"
                                                        onClick={() =>
                                                            handleRecurringAction(
                                                                rp.id,
                                                                'resume'
                                                            )
                                                        }
                                                        title="Retomar recorrência"
                                                    >
                                                        <MdPlayArrow className="text-sm" />
                                                        Retomar
                                                    </button>
                                                )}
                                                {rp.status !==
                                                    'CANCELLED' && (
                                                    <button
                                                        className="btn btn-xs btn-outline btn-error"
                                                        onClick={() =>
                                                            setRecurringConfirm(
                                                                {
                                                                    id: rp.id,
                                                                    action: 'cancel',
                                                                }
                                                            )
                                                        }
                                                        title="Cancelar recorrência"
                                                    >
                                                        <MdCancel className="text-sm" />
                                                        Cancelar
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    )
                                })}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <ConfirmModal
            isOpen={!!recurringConfirm}
            onClose={() => setRecurringConfirm(null)}
            onConfirm={() => {
            if (recurringConfirm) {
                handleRecurringAction(
                    recurringConfirm.id,
                    recurringConfirm.action
                )
            }
            }}
            title={
            recurringConfirm?.action === 'cancel'
                ? 'Cancelar recorrência'
                : 'Pausar recorrência'
            }
            message={
            recurringConfirm?.action === 'cancel'
                ? 'Tem certeza que deseja cancelar esta compra recorrente? Esta ação não pode ser desfeita.'
                : 'Tem certeza que deseja pausar esta compra recorrente? Você poderá retomá-la depois.'
            }
            confirmButtonText={
            recurringConfirm?.action === 'cancel'
                ? 'Cancelar recorrência'
                : 'Pausar recorrência'
            }
            cancelButtonText="Voltar"
            loading={recurringActionLoading}
            />
        </>
    )
}
